import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BlogCard from "./BlogCard";
import { Helmet } from "react-helmet-async";

const API = process.env.REACT_APP_API_URL;

const SKELETON_COUNT = 6;

const Blogs = ({ currentUser }) => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ ADMIN CHECK (from global user)
const isAdmin = currentUser && currentUser.role === "admin";

  useEffect(() => {
    fetch(`${API}/api/blogs`)
      .then((res) => res.json())
      .then((data) => {
        setBlogs(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        setBlogs([]);
        setLoading(false);
      });
  }, []);

  const handleDelete = async (id) => {
  const token =
    localStorage.getItem("token") ||
    sessionStorage.getItem("token");

  if (!window.confirm("Delete this blog?")) return;

  try {
    await fetch(`${API}/api/blogs/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // update UI instantly
    setBlogs((prev) => prev.filter((b) => b._id !== id));
  } catch (err) {
    console.error(err);
  }
};

  return (
    <>
      <Helmet>
        <title>Travel Blogs & Guides | Expeditio</title>
        <meta name="description" content="Smart travel insights, hidden gems, budget tips, and destination guides — curated for every kind of explorer." />
        <link rel="canonical" href="https://www.expeditio.world/blogs" />
        <meta property="og:title" content="Travel Blogs & Guides | Expeditio" />
        <meta property="og:description" content="Smart travel insights, hidden gems, budget tips, and destination guides." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Travel Blogs & Guides",
          description: "Smart travel insights, hidden gems, budget tips, and destination guides",
          url: "https://www.expeditio.world/blogs",
          numberOfItems: blogs.length,
          itemListElement: blogs.slice(0, 10).map((b, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: b.title,
            url: `https://www.expeditio.world/blogs/${b.slug}`,
          })),
        })}</script>
      </Helmet>
      <section className="bg-[#fdfcf7] min-h-screen px-4 pt-32 pb-28">
        <div className="max-w-6xl mx-auto space-y-14">

        {/* HEADER */}
        <div className="flex flex-col items-center text-center space-y-4">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full
                           bg-[#5b6f00]/10 text-[#5b6f00] text-xs font-semibold
                           tracking-widest uppercase">
            ✍️ &nbsp;The Blog
          </span>

          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900
                         leading-tight tracking-tight max-w-xl">
            Travel Blogs &amp; Guides
          </h1>

          <p className="text-base text-gray-500 max-w-md leading-relaxed">
            Smart travel insights, hidden gems, budget tips, and destination
            guides — curated for every kind of explorer.
          </p>

          <div className="w-12 h-1 rounded-full bg-[#5b6f00]/30 mt-1" />
        </div>

        {/* ✅ ADMIN BUTTON */}
        {isAdmin && (
          <div className="flex justify-end">
            <button
              onClick={() => navigate("/blogs/admin")}
              className="inline-flex items-center gap-2 rounded-full
                         bg-[#5b6f00] px-5 py-2.5 text-white text-sm
                         font-semibold shadow-md hover:bg-[#4a5c00]
                         hover:-translate-y-0.5 hover:shadow-lg
                         transition-all duration-200"
            >
              <span className="text-base leading-none">🛠</span>
              Add New Blog
            </button>
          </div>
        )}

        {/* GRID */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <BlogCard key={i} skeleton />
            ))}
          </div>
        ) : blogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center
                          py-24 space-y-5 text-center">
            <div className="text-5xl opacity-30">📭</div>
            <h2 className="text-xl font-semibold text-gray-700">
              No articles yet
            </h2>
            <p className="text-sm text-gray-400 max-w-xs">
              We're working on something great. Check back soon!
            </p>

            {isAdmin && (
              <button
                onClick={() => navigate("/blogs/admin")}
                className="mt-2 rounded-full bg-[#5b6f00] px-6 py-2.5
                           text-white text-sm font-semibold shadow
                           hover:bg-[#4a5c00] transition-colors duration-200"
              >
                Publish the first blog →
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {blogs.map((blog) => (
             <BlogCard
                key={blog._id}
                blog={blog}
                isAdmin={isAdmin}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </section>
    </>
  );
};

export default Blogs;